import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PersonSearchResultDto } from '../services/api.service';
import { RouteTypeService } from '../services/route-type.service';
import { RouteType } from '../constants';
import { BulkEinFormatterModalComponent } from '../professional-development/pd-advanced-person-search/bulk-ein-formatter-modal/bulk-ein-formatter-modal.component';

export interface PersonSearchParams {
  firstName: string;
  lastName: string;
  ein: string;
  searchAllPersons: boolean;
}

@Component({
  selector: 'cert-voucher-person-search',
  standalone: false,
  templateUrl: './voucher-person-search.component.html',
})
export class VoucherPersonSearchComponent implements OnChanges, OnInit {
  @Input() results: PersonSearchResultDto[] = [];
  @Input() searching = false;
  @Input() selectedPersonIds: number[] = [];
  @Input() unmatchedEins: string[] = [];

  @Output() search = new EventEmitter<PersonSearchParams>();
  @Output() bulkSearch = new EventEmitter<{ eins: string[]; searchAllPersons: boolean }>();
  @Output() addAll = new EventEmitter<void>();
  @Output() select = new EventEmitter<PersonSearchResultDto>();
  @Output() remove = new EventEmitter<number>();

  firstName = '';
  lastName = '';
  ein = '';
  bulkEinInput = '';
  searchPerformed = false;
  searchComplete = false;

  isEppPortal!: boolean;
  searchAllPersons = false;
  RouteType = RouteType;

  constructor(
    private readonly routeTypeService: RouteTypeService,
    private readonly modalService: NgbModal,
  ) {}

  ngOnInit() {
    this.isEppPortal = this.routeTypeService.getRouteType() === RouteType.Epp;
    this.searchAllPersons = this.isEppPortal;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['results']) {
      const previous: PersonSearchResultDto[] = changes['results'].previousValue ?? [];
      const current: PersonSearchResultDto[] = changes['results'].currentValue ?? [];
      // Results cleared externally (e.g. after creating credits) — not a failed search.
      if (!this.searching && previous.length > 0 && current.length === 0) {
        this.searchPerformed = false;
        this.searchComplete = false;
        return;
      }
    }
    if (this.searchPerformed && (changes['results'] || changes['unmatchedEins'])) {
      this.searchComplete = true;
    }
  }

  onSearch() {
    if (!this.firstName && !this.lastName && !this.ein) return;
    this.searching = true;
    this.searchPerformed = true;
    this.searchComplete = false;
    this.search.emit({
      firstName: this.firstName,
      lastName: this.lastName,
      ein: this.ein,
      searchAllPersons: this.searchAllPersons,
    });
  }

  onClear() {
    this.firstName = '';
    this.lastName = '';
    this.ein = '';
    this.searchAllPersons = false;
    this.searchPerformed = false;
    this.searchComplete = false;
    this.searching = false;
    this.search.emit({ firstName: '', lastName: '', ein: '', searchAllPersons: false });
  }

  onBulkClear() {
    this.bulkEinInput = '';
    this.searchPerformed = false;
    this.searchComplete = false;
    this.searching = false;
    this.search.emit({ firstName: '', lastName: '', ein: '', searchAllPersons: false });
  }

  onBulkSearch() {
    const eins = this.parseBulkEins();
    if (eins.length > 0) {
      this.searching = true;
      this.searchPerformed = true;
      this.searchComplete = false;
      this.bulkSearch.emit({ eins: eins, searchAllPersons: this.searchAllPersons });
    }
  }

  onAddAll() {
    this.addAll.emit();
  }

  isSelected(personId: number): boolean {
    return this.selectedPersonIds.includes(personId);
  }

  openBulkFormatterModal() {
    this.modalService.open(BulkEinFormatterModalComponent, {
      backdrop: 'static',
      size: 'lg',
      centered: true,
    });
  }

  private parseBulkEins(): string[] {
    return this.bulkEinInput
      .split(',')
      .map((s) => s.trim().toUpperCase())
      .filter((s) => s.length > 0);
  }
}
